import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, DragControls } from 'framer-motion';
import { X, Minus, Send, GripHorizontal } from 'lucide-react';
import { useAskKiki } from './context/AskKikiContext';
import type { Message } from './context/AskKikiContext';
import MessageBubble from './MessageBubble';
import TicketEscalation from './TicketEscalation';
import InlineNav from './components/InlineNav';
import InlineFAQ from './components/InlineFAQ';
import type { NavNode } from './config/navigationConfig';
import KikiBotIcon from '../../assets/Chat-bot_widget.png';
import { askKikiService } from '../../services/askKikiService';
import { useTelemetry } from '../../hooks/useTelemetry';
import { MODULES } from '../../telemetry/constants/modules';
import { ACTIONS } from '../../telemetry/constants/actions';

interface ChatWindowProps {
  dragControls: DragControls;
}

const ChatWindow: React.FC<ChatWindowProps> = ({ dragControls }) => {
  const {
    setIsOpen, messages, addMessage, isTyping, setIsTyping,
    setUnreadCount
  } = useAskKiki();
  const { trackAction } = useTelemetry({
    module: MODULES.AI_ASSISTANT,
    submodule: 'Chat Window',
  });
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const windowRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    setShowScrollTop(scrollTop > 50);
    setShowScrollBottom(scrollHeight - scrollTop - clientHeight > 50);
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'A') {
      const href = target.getAttribute('href');
      if (href && href.startsWith('/')) {
        e.preventDefault();
        navigate(href);
        setIsOpen(false); // Close chat when navigating
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (windowRef.current && !windowRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        trackAction(ACTIONS.CLOSE_CHATBOT, 'Close AI Chat via click outside');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsOpen]);

  useEffect(() => {
    // Reset unread count when opened
    setUnreadCount(0);
  }, [setUnreadCount]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const handleReturnToMainMenu = () => {
    handleSend('Return to Main Menu');
  };

  const handleSend = async (text: string = inputValue) => {
    if (!text.trim()) return;

    trackAction(ACTIONS.SEND_MESSAGE, 'Send AI Chat Message', { messageLength: text.length });
    setInputValue('');
    addMessage({ sender: 'user', text, type: 'text' });

    // Handle specific "hi" / main menu restart
    const normalized = text.trim().toLowerCase();
    const isMainMenu = [
      'hi', 'hello', 'hey', 'menu', 'main menu',
      'return to main menu', 'back to main menu',
      'return to main-menu', 'back to main-menu',
      'start over', 'restart'
    ].includes(normalized);

    if (isMainMenu) {
      addMessage({
        sender: 'bot',
        text: '👋 Welcome to Marketing 4SIGHT\nI am Kiki, your smart marketing buddy.\nChoose a topic below or ask me anything about the platform, pricing, onboarding, or support.',
        type: 'text'
      });
      setTimeout(() => {
        addMessage({ sender: 'bot', text: 'root', type: 'guided_nav' });
      }, 300);
      return;
    }

    setIsTyping(true);

    try {
      const isPublic = !localStorage.getItem('access_token') || window.location.pathname === '/login';
      const response = await askKikiService.sendMessage(text, isPublic);
      addMessage({ sender: 'bot', text: response.text, type: response.type });
    } catch (error: any) {
      if (error?.message !== 'ABORTED') {
        addMessage({ sender: 'bot', text: 'Sorry, I encountered an error.', type: 'text' });
      }
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNodeSelect = (node: NavNode) => {
    if (node.id === 'root' || node.title === 'Return to Main Menu') {
      handleReturnToMainMenu();
      return;
    }

    if (node.type === 'prompt' && node.action) {
      handleSend(node.action);
      return;
    }

    // Add user's choice to chat
    addMessage({ sender: 'user', text: node.title, type: 'text' });

    if (node.type === 'category') {
      if (node.id === 'seo-other' || node.id === 'smm-other' || node.id === 'gmb-other' || node.id === 'wb-other' || node.id === 'onb-other') {
        setTimeout(() => {
          addMessage({ sender: 'bot', text: "No problem! Since your question falls outside those areas, just type the question you'd like to ask. Otherwise, please feel free to raise a ticket.", type: 'text' });
          setTimeout(() => {
            addMessage({ sender: 'bot', text: node.id, type: 'guided_nav' });
          }, 300);
        }, 300);
      } else if (node.id === 'seo-manager' || node.id === 'social-media-manager' || node.id === 'gmb-manager' || node.id === 'website-builder' || node.id === 'onboarding') {
        setTimeout(() => {
          addMessage({ sender: 'bot', text: `Got it! You're looking for help with the ${node.title}. Which specific area are you focusing on right now?`, type: 'text' });
          setTimeout(() => {
            addMessage({ sender: 'bot', text: node.id, type: 'guided_nav' });
          }, 300);
        }, 300);
      } else {
        // Simulate bot replying with the sub-menu immediately
        setTimeout(() => {
          addMessage({ sender: 'bot', text: node.id, type: 'guided_nav' });
        }, 300);
      }
    } else if (node.type === 'route' && node.action) {
      // route logic (would connect to react-router)
      navigate(node.action);
      setIsOpen(false);
    } else if (node.type === 'callback' && node.action) {
      const isPublic = !localStorage.getItem('access_token') || window.location.pathname === '/login';
      if (node.action === 'PLATFORM_SUB_CATEGORY') {
        const navToFaqMap: Record<string, string> = {
          // ── Onboarding ──────────────────────────────────────────
          'onb-overview': 'module-1-sub-1',
          'onb-business-info': 'module-1-sub-2',
          'onb-ecosystem': 'module-1-sub-3',
          'onb-business-portfolio': 'module-1-sub-4',
          'onb-terms': 'module-1-sub-5',
          'onb-post-onboarding': 'module-1-sub-6',
          // ── Profile ─────────────────────────────────────────────
          'prof-contact-identity': 'module-2-sub-1',
          'prof-business-details': 'module-2-sub-2',
          'prof-subscription-billing': 'module-2-sub-3',
          'prof-help-centre': 'module-2-sub-4',
          'prof-data-behaviour': 'module-2-sub-5',
          // ── Website Builder ──────────────────────────────────────
          'wb-access-overview': 'module-3-sub-1',
          'wb-site-structure': 'module-3-sub-2',
          'wb-page-content': 'module-3-sub-3',
          'wb-templates-theme': 'module-3-sub-4',
          'wb-seo-foundation': 'module-3-sub-5',
          'wb-analytics': 'module-3-sub-6',
          'wb-output-package': 'module-3-sub-7',
          'wb-integrations': 'module-3-sub-8',
          // ── GMB Manager ─────────────────────────────────────────
          'gmb-overview': 'module-4-sub-1',
          'gmb-classification': 'module-4-sub-2',
          'gmb-setup': 'module-4-sub-3',
          'gmb-competition': 'module-4-sub-4',
          'gmb-strategy': 'module-4-sub-5',
          'gmb-implementation': 'module-4-sub-6',
          'gmb-governance': 'module-4-sub-7',
          'gmb-optimization': 'module-4-sub-8',
          'gmb-keywords': 'module-4-sub-9',
          'gmb-edge-cases': 'module-4-sub-10',
          // ── Social Media Manager ─────────────────────────────────
          'smm-general': 'module-5-sub-1',
          'smm-setup': 'module-5-sub-2',
          'smm-brand-comms': 'module-5-sub-3',
          'smm-content-plan': 'module-5-sub-4',
          'smm-strategy': 'module-5-sub-5',
          'smm-implementation': 'module-5-sub-6',
          'smm-governance': 'module-5-sub-7',
          'smm-optimization': 'module-5-sub-8',
          'smm-plans': 'module-5-sub-9',
          // ── SEO Manager ─────────────────────────────────────────
          'seo-general': 'module-6-sub-1',
          'seo-activation': 'module-6-sub-2',
          'seo-strategy': 'module-6-sub-3',
          'seo-implementation': 'module-6-sub-4',
          'seo-governance': 'module-6-sub-5',
          'seo-optimization': 'module-6-sub-6',
          // ── Utility Manager (Keyword Planner) ───────────────────
          'um-what-is': 'module-7-sub-1',
          'um-where-from': 'module-7-sub-2',
          'um-how-built': 'module-7-sub-3',
          'um-scoring': 'module-7-sub-4',
          'um-groups': 'module-7-sub-5',
          'um-competitor': 'module-7-sub-6',
          'um-local-online': 'module-7-sub-7',
          'um-new-business': 'module-7-sub-8',
          // ── Ad-Hoc Content Creator ───────────────────────────────
          'adhoc-overview': 'module-8-sub-0',
          'adhoc-user-types': 'module-8-sub-1',
          'adhoc-brand-intel': 'module-8-sub-2',
          'adhoc-content-types': 'module-8-sub-3',
          'adhoc-static-post': 'module-8-sub-4',
          'adhoc-infographic': 'module-8-sub-5',
          'adhoc-carousel': 'module-8-sub-6',
          'adhoc-video': 'module-8-sub-7',
          'adhoc-linkedin': 'module-8-sub-8',
          'adhoc-blog': 'module-8-sub-9',
          'adhoc-whitepaper': 'module-8-sub-10',
          'adhoc-case-study': 'module-8-sub-11',
          'adhoc-landing-page': 'module-8-sub-12',
          // Print & Offline Formats — combined sub-modules 13–21
          'adhoc-print': 'module-8-sub-13',
          'adhoc-presentations': 'module-8-sub-22',
          'adhoc-common': 'module-8-sub-24',
        };
        const subModuleId = navToFaqMap[node.id];
        setTimeout(() => {
          addMessage({
            sender: 'bot',
            text: `Here are the FAQs for **${node.title}**:`,
            type: 'text',
          });
          if (subModuleId) {
            addMessage({ sender: 'bot', text: subModuleId, type: 'faq' });
          }
        }, 300);

      } else if (node.action === 'CHAT_WITH_AGENT') {
        setTimeout(() => {
          addMessage({ sender: 'bot', text: "I'm connecting you to the next available support agent. Please type your issue below, and they will be right with you!", type: 'text' });
        }, 300);
      } else if (node.action === 'RAISE_TICKET') {
        setTimeout(() => {
          if (isPublic) {
            addMessage({ sender: 'bot', text: 'Send the issue you are facing to us via email. Mail us the issue at [support@quantyraanalytics.com](mailto:support@quantyraanalytics.com)', type: 'text' });
          } else {
            addMessage({ sender: 'bot', text: 'To raise a ticket, [click here!](/support/tickets/new)', type: 'text' });
          }
        }, 300);
      } else if (node.action === 'SUP_STATUS') {
        setTimeout(() => {
          if (isPublic) {
            addMessage({ sender: 'bot', text: 'Please check your email inbox for viewing ticket status.', type: 'text' });
          } else {
            addMessage({ sender: 'bot', text: 'To view your ticket status, [click here!](/support/tickets)', type: 'text' });
          }
        }, 300);
      } else if (node.action === 'SUP_FAQ') {
        setTimeout(() => {
          addMessage({ sender: 'bot', text: 'Here are our most commonly asked questions. Select a topic to browse:', type: 'text' });
          addMessage({ sender: 'bot', text: 'module-all', type: 'faq' });
        }, 300);
      } else if (node.action === 'PAY_PRICING') {
        setTimeout(() => {
          if (isPublic) {
            addMessage({ sender: 'bot', text: 'To view pricing plans, [click here!](https://www.marketing4sight.com/pricing)', type: 'text' });
          } else {
            addMessage({ sender: 'bot', text: 'To view pricing plans, [click here!](/profile?tab=subscription)', type: 'text' });
          }
        }, 300);
      } else if (node.action === 'PAY_BILLING') {
        setTimeout(() => {
          if (isPublic) {
            addMessage({ sender: 'bot', text: 'Please login to view or download your invoice... [click here to log in](/login)', type: 'text' });
          } else {
            addMessage({ sender: 'bot', text: 'To view billing and invoices, [click here!](/profile?tab=subscription)', type: 'text' });
          }
        }, 300);
      } else if (node.action === 'PAY_ISSUES') {
        setTimeout(() => {
          addMessage({ sender: 'bot', text: 'Please describe your issue!', type: 'text' });
        }, 300);
      }
    }
  };

  const renderMessageContent = (msg: Message, index: number) => {
    if (msg.type === 'ticket_escalation') {
      return (
        <div key={msg.id} className="flex flex-col w-full mb-4">
          <div className="pl-10">
            <TicketEscalation onActionSelect={(action) => handleSend(action)} />
          </div>
        </div>
      );
    } else if (msg.type === 'guided_nav') {
      return (
        <div key={msg.id} className="flex flex-col w-full">
          <InlineNav
            nodeId={msg.text}
            onNodeSelect={handleNodeSelect}
            onReturnToMainMenu={handleReturnToMainMenu}
          />
        </div>
      );
    } else if (msg.type === 'faq') {
      return (
        <div key={msg.id} className="flex flex-col w-full">
          <InlineFAQ
            subModuleId={msg.text}
            onReturnToMainMenu={handleReturnToMainMenu}
          />
        </div>
      );
    } else if (msg.type === 'hint') {
      return null;
    }

    const nextMsg = messages[index + 1];
    const isFollowedByNavOrFaq = nextMsg && (nextMsg.type === 'guided_nav' || nextMsg.type === 'faq');
    const showMainMenuButton = msg.sender === 'bot' && msg.id !== 'welcome-msg' && !isFollowedByNavOrFaq;

    return (
      <MessageBubble
        key={msg.id}
        message={msg}
        showMainMenuButton={showMainMenuButton}
        onReturnToMainMenu={handleReturnToMainMenu}
      />
    );
  };

  return (
    <motion.div
      ref={windowRef}
      initial={{ opacity: 0, scale: 0.95, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, y: 20 }}
      className="flex flex-col w-full h-full bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200 z-50"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white cursor-pointer"
        onPointerDown={(e) => dragControls.start(e)}
        style={{ touchAction: 'none' }}
      >
        <div className="flex items-center gap-3 pointer-events-none">
          <div className="w-10 h-10 rounded-full bg-white/20 p-1 flex items-center justify-center overflow-hidden">
            <img src={KikiBotIcon} alt="Kiki" className="w-full h-full object-contain drop-shadow-md" />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-base leading-tight tracking-wide">Ask Kiki</span>
            <span className="text-xs text-blue-100 font-medium">Your smart marketing buddy</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => {
              setIsOpen(false);
              trackAction(ACTIONS.CLOSE_CHATBOT, 'Minimize AI Chat');
            }}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors focus:outline-none"
            aria-label="Minimize"
          >
            <Minus className="w-5 h-5" />
          </button>
          <button
            onClick={() => {
              setIsOpen(false);
              trackAction(ACTIONS.CLOSE_CHATBOT, 'Close AI Chat via X');
            }}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors focus:outline-none"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Body — simple chat */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        onClick={handleLinkClick}
        className="flex-1 overflow-y-auto p-4 bg-[#f8fafc] relative"
      >
        {showScrollTop && (
          <div className="sticky top-0 z-10 flex justify-end mb-2">
            <button
              onClick={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
              className="p-1.5 bg-white/90 border border-gray-200 text-gray-600 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none"
            >
              <span className="text-xs">↑</span>
            </button>
          </div>
        )}

        {messages.map((msg, index) => renderMessageContent(msg, index))}

        {isTyping && (
          <div className="flex w-full mb-4 justify-start items-center">
            <div className="flex-shrink-0 w-8 h-8 rounded-full border border-gray-100 overflow-hidden mr-2 bg-white flex items-center justify-center">
              <img src={KikiBotIcon} alt="Kiki" className="w-6 h-6 object-contain pointer-events-none" />
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-none px-4 py-3 shadow-sm flex gap-1">
              <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0 }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
              <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.2 }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
              <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 0.6, delay: 0.4 }} className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />

        {showScrollBottom && (
          <div className="sticky bottom-0 z-10 flex justify-end mt-2">
            <button
              onClick={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
              className="p-1.5 bg-white/90 border border-gray-200 text-gray-600 rounded-full shadow-sm hover:bg-gray-50 focus:outline-none"
            >
              <span className="text-xs">↓</span>
            </button>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-4 bg-white border-t border-gray-100 flex flex-col">
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 focus-within:ring-2 focus-within:ring-blue-100 focus-within:border-blue-400 transition-all">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask me anything..."
            disabled={isTyping}
            className="flex-1 bg-transparent border-none focus:ring-0 text-sm py-1 disabled:opacity-50 text-gray-700 outline-none placeholder:text-gray-400"
          />
          <button
            onClick={() => handleSend()}
            disabled={!inputValue.trim() || isTyping}
            className="p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:hover:bg-blue-600 focus:outline-none"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="text-center mt-2">
          <span className="text-[10px] text-gray-400">Powered by Marketing4SIGHT</span>
        </div>
      </div>
    </motion.div>
  );
};

export default ChatWindow;
